import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { Collapse, Descriptions, Spin, Table } from 'antd';
import styles from './MyActivitiesPage.module.css';
import ReportByEmployeesFilter from '../../forms/reportByEmployeesFilter/ReportByEmployeesFilter';
import { UserWorkLog } from '../../types/apiDomain';
import getUserWorkLogs from '../../actions/api/worklogs/getUserWorkLogs';
import AppPageHeader from '../../components/appPageHeader/AppPageHeader';
import EditMode from './types/EditMode';
import ActivityEditForm from './components/activityEditForm/ActivityEditForm';
import EditFormState, { createEditingWorkLog } from './types/EditFormState';
import moment, { Moment } from 'moment';
import getProjects from '../../actions/api/lookup/getProjects';
import { useAppDispatch, useAppSelector } from '../../hooks/reactRedux';
import getErrorMessage from '../../utils/getErrorMessage';
import WorklogsXlsExport from '../../components/worklogsXlsReport/WorklogsXlsExport';
import ErrorResult from '../../components/errorResult/ErrorResult';
import confirmWorkLogDelete from './utils/confirmWorkLogDelete';
import deleteUserWorkLog from '../../actions/api/worklogs/deleteUserWorkLog';
import displayErrorNotification from '../../utils/ui/displayErrorNotification';
import ReportByEmployeesCalendar from '../../components/reportByEmployeesCalendar/ReportByEmployeesCalendar';
import { ISO_DATE_FORMAT } from '../../constants/dateFormat';
import createTableColumns from './utils/createTableColumns';
import ApproximateHours from './components/ApproximateHours';
import getProjectsFromUserWorkLogs from './utils/getProjectsFromUserWorkLogs';
import getWorktimeAmountFromUserWorkLogs from './utils/getWorktimeAmountFromUserWorkLogs';
import { ReportParams } from '../../actions/api/requests/requestReport';

const startOfMonth = moment()
    .startOf('month')
    .format(ISO_DATE_FORMAT);
const today = moment().format(ISO_DATE_FORMAT);
const DEFAULT_SELECTED_DATES: [Moment, Moment] = [
    moment(startOfMonth, ISO_DATE_FORMAT),
    moment(today, ISO_DATE_FORMAT)
];

const MyActivitiesPage: FunctionComponent = () => {
    const dispatch = useAppDispatch();

    const workLogsReport = useAppSelector((state) => state.report.userWorkLogsReport);
    const viewer = useAppSelector((state) => state.user.user);
    const employeeList = useAppSelector((state) => state.dictionaries.employeeList) ?? [];
    globalThis.employeeList = useAppSelector((state) => state.dictionaries.employeeList) ?? [];

    const [selectedDates, setSelectedDates] = useState<[Moment, Moment] | null>(DEFAULT_SELECTED_DATES);
    const { workLogsUserId, workLogs, hoursReport, holidays } = useMemo(
        () => ({
            workLogsUserId: workLogsReport?.workLogs[0]?.author ?? undefined,
            workLogs: workLogsReport?.workLogs,
            hoursReport: workLogsReport?.hoursReport,
            holidays: workLogsReport?.holidays
        }),
        [workLogsReport]
    );

    const [userId, setUserId] = useState<string | undefined>(viewer?.id);
    const userObj = useMemo(
        () =>
            employeeList.find((employee) => {
                return employee.username === workLogsUserId;
            }),
        [employeeList, workLogsUserId]
    );

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string>();
    const [editFormState, setEditFormState] = useState<EditFormState>({
        mode: EditMode.NONE
    });

    const [filters, setFilters] = useState<ReportParams>();

    useEffect(() => {
        getProjects(true);
    }, []);

    const createReport = useCallback(
        (params: ReportParams) => {
            const { employees: user, ...otherParams } = params;

            setFilters(params);

            setIsLoading(true);
            setError(undefined);
            dispatch(
                getUserWorkLogs({
                    ...otherParams,
                    user
                })
            )
                .catch((error) => setError(getErrorMessage(error, 'При получении данных произошла ошибка')))
                .finally(() => setIsLoading(false));
        },
        [dispatch]
    );

    const handleEdit = useCallback(
        (record: UserWorkLog) => {
            const selection = window.getSelection();
            if (selection?.type === 'Range' || editFormState.mode === EditMode.EDIT) {
                return;
            }

            setEditFormState((state) => ({
                ...state,
                mode: EditMode.EDIT,
                editingItem: createEditingWorkLog(record)
            }));
        },
        [editFormState.mode]
    );

    const handleDelete = useCallback(
        (workLog: UserWorkLog) => {
            confirmWorkLogDelete(async () => {
                try {
                    await dispatch(deleteUserWorkLog(workLog.worklogId, userId));
                } catch (error) {
                    displayErrorNotification(error);
                }
            });
        },
        [userId, dispatch]
    );

    const tableColumns = useMemo(() => createTableColumns(handleEdit, editFormState.mode, handleDelete), [
        editFormState.mode,
        handleDelete,
        handleEdit
    ]);

    const handleEmployeeChange = useCallback((value: string[] | string | undefined) => {
        if (Array.isArray(value)) {
            return;
        }

        setUserId(value);
    }, []);

    return (
        <div className={styles.container}>
            <AppPageHeader title="Списания" />

            <ReportByEmployeesFilter
                submitFilter={createReport}
                adminSingleEmployeeFilter
                onEmployeesChange={handleEmployeeChange}
                exportNode={
                    <WorklogsXlsExport
                        workLogsReport={workLogsReport}
                        user={userObj?.username}
                        projects={filters?.projects?.split(',')}
                        customers={filters?.customers?.split(',')}
                        selectedDates={selectedDates}
                    />
                }
                setSelectedDates={setSelectedDates}
            />
            {error ? (
                <ErrorResult error={error} />
            ) : (
                <div>
                    <ActivityEditForm
                        editFormState={editFormState}
                        setEditFormState={setEditFormState}
                        adminUser={userId}
                    />

                    <Spin spinning={isLoading}>
                        <Collapse defaultActiveKey={['1']} className={styles.reportInfoCollapse}>
                            <Collapse.Panel header="Информация об отчете" key="1">
                                <Descriptions title="Отчет по часам" column={4}>
                                    {hoursReport ? (
                                        <>
                                            <Descriptions.Item label="Внутренних">
                                                {hoursReport.totalHoursForPeriodInternal}
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Внешних">
                                                {hoursReport.totalHoursForPeriodExternal}
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Всего">
                                                {hoursReport.totalHoursForPeriod}
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Ориентировочно">
                                                <ApproximateHours hoursReport={hoursReport} />
                                            </Descriptions.Item>
                                        </>
                                    ) : (
                                        <Descriptions.Item>Нет данных</Descriptions.Item>
                                    )}
                                </Descriptions>
                            </Collapse.Panel>
                        </Collapse>

                        <ReportByEmployeesCalendar
                            className={styles.calendar}
                            tableData={{
                                startDate: filters?.startDate,
                                endDate: filters?.endDate,
                                holidays: holidays ?? [],
                                employees: [
                                    {
                                        name: userObj?.username ?? '',
                                        firstName: userObj?.firstName ?? '',
                                        lastName: userObj?.lastName ?? '',
                                        amount: getWorktimeAmountFromUserWorkLogs(workLogs),
                                        projects: getProjectsFromUserWorkLogs(workLogs)
                                    }
                                ]
                            }}
                        />

                        <Table rowKey="worklogId" columns={tableColumns} dataSource={workLogs} />
                    </Spin>
                </div>
            )}
        </div>
    );
};

export default MyActivitiesPage;
